import {MediaRule} from '@packages/systems/style/style-sheets';
import {CSSStringWriter, CSSOMWriter} from '@packages/systems/style/writers';
import {Breakpoint} from '@packages/systems/style/media-queries';
import {createMediaRulesFromBreakpoints} from './utils';
import {
  createLocalStyleRulesFromJsonNode,
  createLocalStyleRulesFromElement,
} from './nodes';
import {getSymbolRootIdFromInstance} from '@packages/systems/core/utils/JsonDomUtils';
import {Component, Expressions, Plugins} from '@packages/systems/wfdl';
import {namesEqual, qualify} from '@packages/systems/wfdl-Qualified';
import type {Qualified} from '@packages/systems/wfdl-Qualified';
import {reportBug} from '@packages/utilities/reporter';

// Inlined because importing from @packages/utilities/platform pulls in the barrel
// (platform/index.ts → ComponentMapUtils → DesignerState → flows → ... → dynamo),
// which creates a circular dependency detected by CI. PageComponent.ts itself only
// depends on wfdl + wfdl-Qualified, but the ESLint rule webflow/module-top-level-imports
// forbids deep-importing from @packages/utilities/platform/PageComponent directly.
const pageComponentType = qualify(
  Plugins.name('__SitePlugin'),
  Component.name('page')
);

import type {
  StyleRulesByBreakpointId,
  BreakpointID,
  StyleRule,
} from '@packages/systems/style/types';
import type {JSONNode} from '@packages/shared/types/JSONNode';

export class LocalStyleSSR {
  writer: CSSStringWriter;
  breakpoints: Array<Breakpoint>;
  mediaRules: Map<BreakpointID, MediaRule>;

  constructor(breakpoints: Array<Breakpoint>) {
    this.writer = new CSSStringWriter();
    this.breakpoints = breakpoints;
    this.mediaRules = createMediaRulesFromBreakpoints(breakpoints);
  }

  addRules(localRules: StyleRulesByBreakpointId) {
    for (const key of Object.keys(localRules)) {
      const rules = localRules[key as BreakpointID];

      if (rules && key === 'main') {
        rules.forEach((rule: StyleRule) => this.writer.addRule(rule));
        continue;
      }

      const mediaRule = this.mediaRules.get(key as BreakpointID);

      if (rules && mediaRule) {
        mediaRule.rules.push(...rules);
      }
    }
  }

  addNode(jsonNode: JSONNode, symbolId: string) {
    if (getSymbolRootIdFromInstance(jsonNode)) return;
    this.addRules(createLocalStyleRulesFromJsonNode(jsonNode, symbolId));
  }

  commit() {
    for (const [, mediaRule] of this.mediaRules) {
      if (mediaRule && mediaRule.rules.length) {
        this.writer.addRule(mediaRule);
      }
    }

    return this.writer.commit();
  }
}

export class LocalStyleDesigner {
  writer: CSSOMWriter;
  componentMap: Component.ComponentMap | null | undefined;
  breakpoints: Array<Breakpoint>;

  constructor(node: HTMLStyleElement, breakpoints: Array<Breakpoint>) {
    this.writer = new CSSOMWriter(node);
    this.breakpoints = breakpoints;
  }

  render(componentMap: Component.ComponentMap, pageId: string) {
    if (this.componentMap === componentMap) {
      return;
    }

    // TODO
    // This intermediate workaround is needed because we don't have an interface
    // for components which would allow them to specify what exactly has changed.
    // Also we don't want to do props diffing since this will also yeld a suboptimal performance.
    // In the future we will remove it and will allow actually updating specific values.
    this.writer.clear();

    const mediaRules = createMediaRulesFromBreakpoints(this.breakpoints);

    const processComponent = (
      component: Component.ComponentType,
      name: Qualified<Component.ComponentName>
    ) => {
      try {
        const render = Component.getRender(component);
        const isPage = namesEqual(name, pageComponentType);

        let symbolId: string;
        if (isPage) {
          symbolId = pageId;
        } else {
          const rootElement = Expressions.getElement(render);
          if (rootElement == null) return;
          symbolId = rootElement.id;
        }

        const elements = Expressions.allElements(render);

        for (const id of Object.keys(elements)) {
          const element = elements[id]!;

          // Skip component instances — their internal elements belong to
          // the referenced component and will be processed in its own iteration.
          if (componentMap.has(element.type)) continue;

          const localRules = createLocalStyleRulesFromElement(
            element,
            symbolId
          );

          for (const key of Object.keys(localRules)) {
            const rules = localRules[key as BreakpointID];

            if (rules && key === 'main') {
              rules.forEach((rule: StyleRule) => this.writer.addRule(rule));
              continue;
            }

            const mediaRule = mediaRules.get(key as BreakpointID);
            if (mediaRule && rules) {
              mediaRule.rules.push(...rules);
            }
          }
        }
      } catch (error) {
        reportBug('LocalStyleDesigner.render failure', String(error), {
          team: 'designer-perf',
          ticket: 'https://webflow.atlassian.net/browse/DPERF-363',
          componentName: String(name),
        });
      }
    };

    // Process page component first to preserve CSS cascade order.
    // CSSOMWriter.addRule is last-wins for equal specificity, so the page
    // must be appended before plugins to match the old iteration order.
    const pageComponent = componentMap.get(pageComponentType);
    if (pageComponent) processComponent(pageComponent, pageComponentType);
    componentMap.forEach((component, name) => {
      if (component === pageComponent) return;
      processComponent(component, name);
    });

    for (const [, mediaRule] of mediaRules) {
      if (mediaRule && mediaRule.rules.length) {
        this.writer.addRule(mediaRule);
      }
    }

    this.componentMap = componentMap;

    this.writer.commit();
  }
}
